/**
 * Seat Positioning Utilities — rail-anchored orbit with uniform scale-to-fit.
 */

export function calculateSeatPosition(seatIndex, totalSeats, tableWidth, tableHeight, seatRadius = 120, localPlayerIndex = 0) {
  const centerX = tableWidth / 2;
  const centerY = tableHeight / 2;
  const radiusX = tableWidth / 2;
  const radiusY = tableHeight / 2;
  const baseAngle = -Math.PI / 2;
  const angleOffset = (seatIndex - localPlayerIndex) * (2 * Math.PI) / totalSeats;
  const angle = baseAngle + angleOffset;
  return {
    x: centerX + Math.cos(angle) * (radiusX + seatRadius),
    y: centerY + Math.sin(angle) * (radiusY + seatRadius),
    angle,
  };
}

export function calculateCardPosition(seatX, seatY, seatAngle, cardIndex, tableCenterX, tableCenterY) {
  const dx = seatX - tableCenterX;
  const dy = seatY - tableCenterY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const cardDistance = distance - 120;
  const normalizedDx = dx / distance;
  const normalizedDy = dy / distance;
  const cardWidth = 50;
  const overlapAmount = 30;
  const cardSpacing = cardWidth - overlapAmount;
  const cardOffsetX = -normalizedDy * cardSpacing * (cardIndex === 0 ? -0.5 : 0.5);
  const cardOffsetY = normalizedDx * cardSpacing * (cardIndex === 0 ? -0.5 : 0.5);
  return {
    x: tableCenterX + normalizedDx * cardDistance + cardOffsetX,
    y: tableCenterY + normalizedDy * cardDistance + cardOffsetY,
    rotation: 0,
  };
}

export function calculateChipPosition(seatX, seatY, seatAngle, tableCenterX, tableCenterY) {
  const dx = seatX - tableCenterX;
  const dy = seatY - tableCenterY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const chipDistance = distance * 0.6;
  const normalizedDx = dx / distance;
  const normalizedDy = dy / distance;
  return {
    x: tableCenterX + normalizedDx * chipDistance,
    y: tableCenterY + normalizedDy * chipDistance,
  };
}

export function getTableDimensions(viewportWidth, viewportHeight) {
  const padding = 100;
  const maxWidth = viewportWidth - padding * 2;
  const maxHeight = viewportHeight - padding * 2;
  const aspectRatio = 1.7;
  let width;
  let height;
  if (maxWidth / aspectRatio <= maxHeight) {
    width = maxWidth;
    height = width / aspectRatio;
  } else {
    height = maxHeight;
    width = height * aspectRatio;
  }
  width = Math.max(width, 600);
  height = Math.max(height, 350);
  return { width, height };
}

function getSeatLayoutBounds({
  containerWidth,
  containerHeight,
  paddingTop,
  paddingBottom,
  paddingSide,
  paddingLeft,
  paddingRight,
  centerOffsetX = 0,
  footprintW,
  footprintH,
  viewportWidth,
  viewportHeight,
  viewportScale,
  edgeOverflowTop = 0,
  edgeOverflowBottom = 0,
  isMobile = false,
}) {
  const sideL = paddingLeft ?? paddingSide;
  const sideR = paddingRight ?? paddingSide;
  const contentW = containerWidth - sideL - sideR;
  const contentH = containerHeight - paddingTop - paddingBottom;
  const cx = sideL + contentW / 2 + centerOffsetX;
  const cy = paddingTop + contentH / 2;
  const hw = footprintW / 2;
  const hh = footprintH / 2;

  let minX = sideL;
  let maxX = containerWidth - sideR;
  let minY = paddingTop + edgeOverflowTop;
  let maxY = containerHeight - paddingBottom - edgeOverflowBottom;

  if (viewportScale > 0 && !isMobile) {
    const pad = Math.max(Math.min(sideL, sideR), 6);

    if (viewportWidth) {
      minX = Math.max(minX, pad / viewportScale);
      maxX = Math.min(maxX, (viewportWidth - pad) / viewportScale);
    }

    if (viewportHeight) {
      minY = Math.max(minY, (pad + edgeOverflowTop) / viewportScale);
      maxY = Math.min(maxY, (viewportHeight - pad - edgeOverflowBottom) / viewportScale);
    }
  }

  // Mobile: CSS scale-to-fit can clip the design canvas; keep full seat footprints inside
  // the visible design region (furthest rail distance = edge of that region minus seat box).
  if (isMobile && viewportScale > 0) {
    const edgePad = Math.max(6, Math.ceil(Math.max(hw, hh) * 0.06));

    if (viewportWidth) {
      const visibleW = Math.min(containerWidth, viewportWidth / viewportScale);
      const clipMinX = (containerWidth - visibleW) / 2 + edgePad;
      const clipMaxX = (containerWidth + visibleW) / 2 - edgePad;
      minX = Math.max(minX, clipMinX);
      maxX = Math.min(maxX, clipMaxX);
    }

    if (viewportHeight) {
      const visibleH = Math.min(containerHeight, viewportHeight / viewportScale);
      const clipMinY = (containerHeight - visibleH) / 2 + edgePad;
      const clipMaxY = (containerHeight + visibleH) / 2 - edgePad;
      minY = Math.max(minY, clipMinY);
      maxY = Math.min(maxY, clipMaxY);
    }
  }

  return { cx, cy, minX, maxX, minY, maxY, hw, hh };
}

function seatEdgesFit({ x, y, cx, cy, hw, hh, bounds, edgeOverflowTop = 0, edgeOverflowBottom = 0 }) {
  return (
    cx + x - hw >= bounds.minX &&
    cx + x + hw <= bounds.maxX &&
    cy + y - hh - edgeOverflowTop >= bounds.minY &&
    cy + y + hh + edgeOverflowBottom <= bounds.maxY
  );
}

function seatAngles(totalSeats, anchorIdx) {
  return Array.from({ length: totalSeats }, (_, idx) =>
    ((idx - anchorIdx) * 2 * Math.PI) / totalSeats + Math.PI / 2
  );
}

/** Seat center just outside the felt rail along the outward normal at this angle. */
function seatCenterOnRail(angle, semiA, semiB, hw, hh, railGap) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const bx = semiA * cos;
  const by = semiB * sin;
  const nx = cos / semiA;
  const ny = sin / semiB;
  const nLen = Math.hypot(nx, ny) || 1e-6;
  const ux = nx / nLen;
  const uy = ny / nLen;
  const boxExtent = Math.abs(cos) * hw + Math.abs(sin) * hh;
  const dist = railGap + boxExtent;
  return { x: bx + ux * dist, y: by + uy * dist, angle };
}

function railPositionsAtScale(angles, semiA, semiB, hw, hh, railGap, scaleX, scaleY = scaleX) {
  return angles.map((angle) => {
    const p = seatCenterOnRail(angle, semiA, semiB, hw, hh, railGap);
    return { x: p.x * scaleX, y: p.y * scaleY, angle };
  });
}

function allPositionsFit(positions, bounds, edgeOverflow) {
  const { cx, cy, hw, hh } = bounds;
  return positions.every((p) =>
    seatEdgesFit({ x: p.x, y: p.y, cx, cy, hw, hh, bounds, ...edgeOverflow })
  );
}

/** AABB separation — prevents rail shrink from stacking opposite seats on each other. */
function seatsHaveMinSeparation(positions, hw, hh, minGap = 10) {
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = Math.abs(positions[i].x - positions[j].x);
      const dy = Math.abs(positions[i].y - positions[j].y);
      if (dx < hw * 2 + minGap && dy < hh * 2 + minGap) {
        return false;
      }
    }
  }
  return true;
}

function layoutFullyValid(positions, bounds, edgeOverflow, hw, hh, minGap) {
  return (
    allPositionsFit(positions, bounds, edgeOverflow) &&
    seatsHaveMinSeparation(positions, hw, hh, minGap)
  );
}

/** Largest rail scales (1 = on rail) that keep every seat inside bounds. X/Y independent. */
function fitRailLayout({
  angles,
  semiA,
  semiB,
  hw,
  hh,
  railGap,
  bounds,
  edgeOverflow,
  minRailScale = 1,
  minSeatGap = 10,
}) {
  const atRail = railPositionsAtScale(angles, semiA, semiB, hw, hh, railGap, 1, 1);
  if (layoutFullyValid(atRail, bounds, edgeOverflow, hw, hh, minSeatGap)) {
    return { positions: atRail, railScale: 1, railScaleX: 1, railScaleY: 1, fitsAllSeats: true };
  }

  // Never pull seats inside the felt rail — shrink footprint instead (see calculateOvalSeatLayout).
  if (minRailScale >= 1) {
    return { positions: atRail, railScale: 1, railScaleX: 1, railScaleY: 1, fitsAllSeats: false };
  }

  let scaleY = 1;
  let lo = minRailScale;
  let hi = 1;
  for (let step = 0; step < 52; step++) {
    const mid = (lo + hi) / 2;
    const trial = railPositionsAtScale(angles, semiA, semiB, hw, hh, railGap, 1, mid);
    if (layoutFullyValid(trial, bounds, edgeOverflow, hw, hh, minSeatGap)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  scaleY = lo;

  lo = minRailScale;
  hi = 1;
  for (let step = 0; step < 52; step++) {
    const mid = (lo + hi) / 2;
    const trial = railPositionsAtScale(angles, semiA, semiB, hw, hh, railGap, mid, scaleY);
    if (layoutFullyValid(trial, bounds, edgeOverflow, hw, hh, minSeatGap)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const scaleX = lo;
  const positions = railPositionsAtScale(angles, semiA, semiB, hw, hh, railGap, scaleX, scaleY);

  return {
    positions,
    railScale: Math.min(scaleX, scaleY),
    railScaleX: scaleX,
    railScaleY: scaleY,
    fitsAllSeats: layoutFullyValid(positions, bounds, edgeOverflow, hw, hh, minSeatGap),
  };
}

export function getDesktopOrbitClearance(totalSeats, { isMobile = false } = {}) {
  if (isMobile) {
    return { baseClearance: 18, clearancePerSeat: 3, railGap: 12 };
  }
  if (totalSeats >= 6) {
    return { baseClearance: 10, clearancePerSeat: 1.5, railGap: 12 };
  }
  if (totalSeats >= 4) {
    return { baseClearance: 10, clearancePerSeat: 1, railGap: 10 };
  }
  return { baseClearance: 18, clearancePerSeat: 3, railGap: 14 };
}

export function getDesktopPlayAreaInsets({ isMobile = false, totalSeats = 4 } = {}) {
  if (isMobile) {
    return {
      centerOffsetX: 0,
      paddingTop: 52,
      paddingBottom: 44,
      paddingLeft: 16,
      paddingRight: 16,
    };
  }
  const dense = totalSeats >= 4;
  return {
    centerOffsetX: dense ? 20 : 24,
    paddingTop: dense ? 48 : 60,
    paddingBottom: dense ? 42 : 52,
    paddingLeft: dense ? 28 : 32,
    paddingRight: dense ? 32 : 28,
  };
}

export function readLayoutOverrides() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("gutzLayoutOverrides");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeLayoutOverrides(overrides) {
  if (typeof window === "undefined") return null;
  const payload = { ...overrides, updatedAt: Date.now(), version: 2 };
  window.localStorage.setItem("gutzLayoutOverrides", JSON.stringify(payload));
  try {
    const bc = new BroadcastChannel("gutz-layout");
    bc.postMessage({ type: "updated", payload });
    bc.close();
  } catch {
    /* BroadcastChannel unavailable */
  }
  window.dispatchEvent(new CustomEvent("gutzLayoutOverridesChanged", { detail: payload }));
  return payload;
}

export function clearLayoutOverrides() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("gutzLayoutOverrides");
  try {
    const bc = new BroadcastChannel("gutz-layout");
    bc.postMessage({ type: "cleared" });
    bc.close();
  } catch {
    /* BroadcastChannel unavailable */
  }
  window.dispatchEvent(new CustomEvent("gutzLayoutOverridesChanged", { detail: null }));
}

function resolveOverrides(overrides) {
  return overrides === undefined ? readLayoutOverrides() : overrides;
}

export function resolveDesktopPlayAreaInsets(layoutOpts, { isMobile = false, overrides = undefined } = {}) {
  const saved = resolveOverrides(overrides);
  if (saved && !isMobile) {
    return {
      centerOffsetX: saved.offsetX ?? 20,
      paddingTop: saved.padT ?? 48,
      paddingBottom: saved.padB ?? 42,
      paddingLeft: saved.padL ?? 28,
      paddingRight: saved.padR ?? 32,
    };
  }

  const base = getDesktopPlayAreaInsets({ isMobile, totalSeats: layoutOpts.totalSeats });
  if (isMobile) return base;

  const candidates = [
    base,
    { ...base, centerOffsetX: 14, paddingLeft: 26, paddingRight: 34 },
    { ...base, centerOffsetX: 8, paddingLeft: 24, paddingRight: 28 },
    { ...base, centerOffsetX: 0, paddingLeft: 22, paddingRight: 22 },
  ];

  let best = null;
  for (const insets of candidates) {
    const layout = calculateOvalSeatLayout({ ...layoutOpts, ...insets });
    if (!layout.fitsAllSeats) continue;
    if (!best || layout.railScaleY > best.layout.railScaleY || (layout.railScaleY === best.layout.railScaleY && layout.railScaleX > best.layout.railScaleX)) {
      best = { insets, layout };
    }
  }

  return best?.insets ?? base;
}

export function calculateOvalSeatLayout({
  totalSeats,
  localPlayerIndex = 0,
  tableWidth = 536,
  tableHeight = 291,
  tableBorder = 8,
  containerWidth,
  containerHeight,
  paddingTop = 100,
  paddingBottom = 82,
  paddingSide = 14,
  maxSeatWidth = 167,
  maxSeatHeight = 193,
  visualScaleMe = 1.06,
  baseClearance = 24,
  clearancePerSeat = 5,
  viewportWidth,
  viewportHeight,
  viewportScale,
  edgeOverflowTop = 22,
  edgeOverflowBottom = 8,
  centerOffsetX = 0,
  paddingLeft,
  paddingRight,
  railGap = 12,
  overrides = undefined,
  isMobile = false,
}) {
  if (totalSeats <= 0) {
    return {
      outerA: 0,
      outerB: 0,
      positions: [],
      fitsAllSeats: true,
      playOffsetX: centerOffsetX,
      railScale: 1,
    };
  }

  const saved = isMobile ? null : resolveOverrides(overrides);
  const effectiveRailGap = saved?.railGap ?? railGap;
  const effectiveOverflowTop = saved?.overflowT ?? edgeOverflowTop;
  const effectiveOverflowBottom = saved?.overflowB ?? edgeOverflowBottom;
  const effectiveViewportWidth = saved?.vpW ?? viewportWidth;
  const effectiveViewportHeight = saved?.vpH ?? viewportHeight;
  const effectiveVisualScaleMe = saved?.visualScaleMe ?? visualScaleMe;
  const mobileFootprintPad = isMobile ? { w: 20, h: 42 } : { w: 0, h: 0 };
  const baseFootprintW = maxSeatWidth * effectiveVisualScaleMe + mobileFootprintPad.w;
  const baseFootprintH = maxSeatHeight * effectiveVisualScaleMe + mobileFootprintPad.h;
  const edgeOverflow = { edgeOverflowTop: effectiveOverflowTop, edgeOverflowBottom: effectiveOverflowBottom };

  const feltW = tableWidth + tableBorder * 2;
  const feltH = tableHeight + tableBorder * 2;
  const semiA = feltW / 2;
  const semiB = feltH / 2;

  const clearance = baseClearance + totalSeats * clearancePerSeat;
  const gap = Math.max(effectiveRailGap, clearance * 0.45);
  const minSeatGap = Math.max(8, Math.round(gap * 0.6));

  const anchorIdx = localPlayerIndex >= 0 ? localPlayerIndex : 0;
  const angles = seatAngles(totalSeats, anchorIdx);

  let footprintScale = 1;
  let footprintW = baseFootprintW;
  let footprintH = baseFootprintH;
  let hw = footprintW / 2;
  let hh = footprintH / 2;
  let bounds;
  let fitted;

  for (let attempt = 0; attempt < 28; attempt++) {
    footprintW = baseFootprintW * footprintScale;
    footprintH = baseFootprintH * footprintScale;
    hw = footprintW / 2;
    hh = footprintH / 2;

    bounds = getSeatLayoutBounds({
      containerWidth: containerWidth || tableWidth * 2,
      containerHeight: containerHeight || tableHeight * 2,
      paddingTop,
      paddingBottom,
      paddingSide,
      paddingLeft,
      paddingRight,
      centerOffsetX,
      footprintW,
      footprintH,
      viewportWidth: effectiveViewportWidth,
      viewportHeight: effectiveViewportHeight,
      viewportScale,
      edgeOverflowTop: effectiveOverflowTop,
      edgeOverflowBottom: effectiveOverflowBottom,
      isMobile,
    });

    fitted = fitRailLayout({
      angles,
      semiA,
      semiB,
      hw,
      hh,
      railGap: gap,
      bounds,
      edgeOverflow,
      minRailScale: isMobile ? 0.72 : 1,
      minSeatGap,
    });

    if (fitted.fitsAllSeats) {
      break;
    }

    footprintScale *= 0.94;
    if (footprintScale < 0.48) {
      break;
    }
  }

  const outerA = Math.max(...fitted.positions.map((p) => Math.abs(p.x)), 0);
  const outerB = Math.max(...fitted.positions.map((p) => Math.abs(p.y)), 0);

  return {
    outerA,
    outerB,
    positions: fitted.positions,
    footprintW,
    footprintH,
    footprintScale,
    bounds,
    fitsAllSeats: fitted.fitsAllSeats,
    playOffsetX: centerOffsetX,
    feltSemiA: semiA,
    feltSemiB: semiB,
    railScale: fitted.railScale,
    railScaleX: fitted.railScaleX,
    railScaleY: fitted.railScaleY,
  };
}

export function getPlayerCountSeatScale(totalSeats, { isMobile = false, overrides = undefined } = {}) {
  const saved = resolveOverrides(overrides);
  if (saved?.seatScale && !isMobile) {
    return saved.seatScale;
  }
  const n = Math.max(2, Math.min(7, totalSeats || 2));
  const mobile = { 2: 0.88, 3: 0.84, 4: 0.80, 5: 0.76, 6: 0.72, 7: 0.68 };
  const desktop = { 2: 1.16, 3: 1.08, 4: 1.02, 5: 1.0, 6: 0.98, 7: 0.96 };
  const map = isMobile ? mobile : desktop;
  return map[n] ?? 1;
}

export function getVerticalSeatBoost(availHeight, isMobile = false) {
  if (isMobile || !availHeight) return 1;
  const ref = 680;
  const ratio = availHeight / ref;
  return Math.max(0.92, Math.min(1.06, 0.98 + (ratio - 1) * 0.1));
}

/** Depth sort: seats higher on screen (negative y) render above lower seats. */
export function getSeatDepthZ(seatY, { isWinner = false } = {}) {
  if (isWinner) return 12;
  return Math.max(2, Math.min(10, Math.round(6 - seatY / 42)));
}

/** @deprecated Use calculateOvalSeatLayout */
export function calculateCircularSeatLayout(opts) {
  return calculateOvalSeatLayout(opts);
}
